import { useEffect, useMemo, useState } from 'react';

import { tracks } from '@/lib/db';

import { buildRoute, type TrackRoute } from './overlay';

/**
 * Simplified geometry for many tracks at once, loaded a track at a time and
 * kept between visits to the page.
 *
 * The flights overview draws every flight a pilot has, and their points live in
 * one row per track — so there is no single query that answers "give me every
 * track's shape". This walks them, reduces each one the moment it arrives
 * (`buildRoute`), and keeps only the reduced line: a 20k-fix track is ~500 kB
 * of typed arrays and a few kB once simplified, so the pile that stays in
 * memory is the small one. The full arrays are never all held at once.
 *
 * It reports progress rather than waiting for the lot, because on a few hundred
 * flights the difference between "a map that fills in over a second" and "a
 * grey rectangle, then a map" is the difference between the page feeling alive
 * and feeling broken.
 */

/**
 * Reduced routes, by track id, across every mount.
 *
 * A track is immutable once imported — re-importing the file makes a new one —
 * so a cached route can never go stale, only unused. Nothing prunes by "is this
 * id still in the list", deliberately: the overview hands this hook every
 * track, and the *drawn* set narrows as the pilot searches. Dropping what falls
 * out of a search would mean re-reading it from IndexedDB the moment they
 * cleared the search box.
 *
 * `null` records a track with nothing drawable (no points row, or fewer than
 * two fixes) so it is not read again on every render.
 */
const routeCache = new Map<string, TrackRoute | null>();

/** Track ids currently being read, so two mounts never read the same row twice. */
const inFlight = new Set<string>();

/**
 * When the cache is thrown away wholesale. Well past any logbook this app is
 * built for (a few kB a track), and there only so a very long-lived tab that
 * has imported and deleted thousands of tracks cannot grow without bound.
 */
const CACHE_LIMIT = 4000;

/** How many tracks are read before the map is told to redraw. */
const FLUSH_EVERY = 12;

/**
 * Bumped every time the requested set of ids changes, or the page is left.
 *
 * A loop that finds `generation` has moved on stops where it is and leaves the
 * rest of its ids un-read; they go back on the queue next time. This is a
 * counter rather than the usual captured `cancelled` flag because the flag a
 * cleanup function sets is, to TypeScript's control-flow analysis inside the
 * loop's own closure, still the `false` it was initialised to.
 */
let generation = 0;

export interface TrackRoutesState {
  /** Reduced geometry for the ids that have loaded so far. */
  byId: ReadonlyMap<string, TrackRoute>;
  /** How many of the requested ids are still to be read. */
  pending: number;
}

export function useTrackRoutes(trackIds: readonly string[] | null): TrackRoutesState {
  // Joined rather than used directly as a dependency: the caller rebuilds this
  // array on every live-query fire, and re-running the loader for an identical
  // list of ids would re-read IndexedDB for nothing.
  const idsKey = trackIds === null ? '' : trackIds.join(',');
  const [loadedVersion, setLoadedVersion] = useState(0);

  useEffect(() => {
    if (trackIds === null || trackIds.length === 0) {
      return;
    }
    generation += 1;
    const mine = generation;
    const missing = trackIds.filter((id) => !routeCache.has(id) && !inFlight.has(id));
    if (missing.length === 0) {
      return;
    }
    for (const id of missing) {
      inFlight.add(id);
    }

    void (async () => {
      let sinceFlush = 0;
      for (const id of missing) {
        if (mine !== generation) {
          break;
        }
        try {
          const points = await tracks.getPoints(id);
          if (routeCache.size >= CACHE_LIMIT) {
            routeCache.clear();
          }
          routeCache.set(id, points === undefined ? null : buildRoute(id, points));
        } finally {
          inFlight.delete(id);
        }
        sinceFlush += 1;
        if (sinceFlush >= FLUSH_EVERY) {
          sinceFlush = 0;
          setLoadedVersion((version) => version + 1);
        }
      }
      if (mine === generation) {
        setLoadedVersion((version) => version + 1);
      }
    })();

    return () => {
      generation += 1;
      /*
       * Released here rather than where the abandoned loop notices it has been
       * overtaken, and that ordering is the whole point: React runs this
       * cleanup before the next effect works out what is missing, so an id the
       * old loop never got to is free again by the time the new one asks. Doing
       * it after the `await` instead left those ids marked in-flight, the new
       * run skipped them as "someone else is reading that", and nobody was —
       * so they stayed undrawn until something else changed.
       */
      for (const id of missing) {
        if (!routeCache.has(id)) {
          inFlight.delete(id);
        }
      }
    };
    // `idsKey` is exactly the contents of `trackIds`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  return useMemo(() => {
    const byId = new Map<string, TrackRoute>();
    let pending = 0;
    for (const id of trackIds ?? []) {
      const cached = routeCache.get(id);
      if (cached === undefined) {
        pending += 1;
      } else if (cached !== null) {
        byId.set(id, cached);
      }
    }
    return { byId, pending };
    // `loadedVersion` is what says the cache moved on; `idsKey` is the id list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, loadedVersion]);
}

/** Test seam: forget everything reduced so far. Nothing in the app calls this. */
export function clearTrackRouteCache(): void {
  routeCache.clear();
  inFlight.clear();
  generation += 1;
}
